// Custom Graph Renderer（GraphRendererインターフェース実装）

import React from 'react';
import { GraphRenderer, GraphCallbacks } from '../../../../types/graph-builder.types';
import { CustomGraphConfig } from '../../../../types/custom-graph.types';
import { DataSet } from '../../../../types';
import { CustomGraphRenderer as CustomGraphRendererComponent } from './CustomGraphRendererComponent';

/**
 * Custom GraphのGraphRendererインターフェース実装
 */
export const customGraphRenderer: GraphRenderer<CustomGraphConfig> = {
  /**
   * 設定の妥当性を検証
   */
  validate(config: CustomGraphConfig, dataset: DataSet): string | null {
    // レイヤーが存在するかチェック
    if (!config.layers || config.layers.length === 0) {
      return 'At least one layer is required';
    }

    const layer = config.layers[0];

    // aestheticマッピングの解決
    const aes = {
      ...config.globalAes,
      ...layer.aes,
    };

    // Stat typeを取得（デフォルトはidentity）
    const statType = layer.stat?.type || 'identity';
    const isCountOrBinStat = statType === 'count' || statType === 'bin';
    const isDensityStat = statType === 'density';

    // X は常に必須
    if (!aes.x) {
      return 'X aesthetic is required';
    }

    // Y は Count/Bin/Density 以外の stat で必須
    // Count/Bin/DensityはY値を自動生成する
    if (!isCountOrBinStat && !isDensityStat && !aes.y) {
      return 'Y aesthetic is required';
    }

    // Xカラムが存在するかチェック
    const xColumnId = aes.x as string;
    const xColumn = dataset.schema.columns.find(col => col.id === xColumnId);
    if (!xColumn) {
      return `X column not found: ${xColumnId}`;
    }

    // Count/Bin/Density stat以外の場合のみYカラムをチェック
    // これらの統計変換はY値を自動生成するため、Y列は不要
    if (!isCountOrBinStat && !isDensityStat) {
      const yColumnId = aes.y as string;
      const yColumn = dataset.schema.columns.find(col => col.id === yColumnId);
      if (!yColumn) {
        return `Y column not found: ${yColumnId}`;
      }

      // Tile geomの場合はカテゴリカル軸をサポート（数値型チェックをスキップ）
      // Issue#243: Tile (Heatmap)で順序尺度（Ordinal）軸をサポート
      const isTileGeom = layer.geom.type === 'tile';
      if (!isTileGeom) {
        // Identity/Smooth statの場合、X, Y両方が数値型である必要がある
        if (xColumn.type !== 'int64' && xColumn.type !== 'float64') {
          return `X column must be numeric, but got ${xColumn.type}`;
        }
        if (yColumn.type !== 'int64' && yColumn.type !== 'float64') {
          return `Y column must be numeric, but got ${yColumn.type}`;
        }
      }
    } else {
      // Count statの場合、X はカテゴリカルでもOK
      // Bin statの場合、X は数値型である必要がある
      if (statType === 'bin') {
        if (xColumn.type !== 'int64' && xColumn.type !== 'float64') {
          return `X column must be numeric for Bin stat, but got ${xColumn.type}`;
        }
      }
      // Count statの場合は型制限なし（カテゴリカル、数値どちらでもOK）
    }

    return null;
  },

  /**
   * グラフをレンダリング
   */
  render(config: CustomGraphConfig, dataset: DataSet, callbacks: GraphCallbacks): React.ReactElement {
    // CustomGraphRendererComponentに処理を委譲
    return React.createElement('div', { style: { width: '100%', height: '100%' } },
      React.createElement(CustomGraphRendererComponent, {
        config,
        dataset,
        selectedRows: callbacks.selectedRows,
        onPointClick: callbacks.onElementSelect,
      })
    );
  },
};
